// src/web/occluder.js
//
// MediaPipe-native face occluder.
//
// Geometry = the fixed canonical face vertices (468) + canonical topology.
// Per frame we set this object's matrix to MediaPipe's transformation matrix M,
// which maps the canonical face onto the runtime face. The mesh therefore lands
// on the user's face surface in the SAME metric space the glasses are posed in
// (the glasses use M·A). Because both are posed by the same M, the depth buffer
// is automatically consistent — no unprojection, no per-vertex depth guessing,
// no depth bias tuning.
//
// Material: colorWrite false + depthWrite true → invisible but occluding.
// Rendered before the glasses (renderOrder 0 vs 10).

import {
  BufferGeometry, BufferAttribute, Mesh, MeshBasicMaterial, DoubleSide
} from 'three';
import { CANONICAL_FACE_VERTICES, CANONICAL_VERTEX_COUNT } from './canonicalFace.js';
import { warpedCanonical } from '../core/noseProfile.js';
import { FACEMESH_TESSELATION_INDICES, FACEMESH_MINUS_LENS_INDICES } from './faceMeshTopology.js';

export class Occluder {
  constructor() {
    this.geometry = new BufferGeometry();

    // Static canonical positions, shrunk slightly toward the face centroid so
    // the occluder sits JUST UNDER the skin. Without this, temple arms that
    // graze the side of the face (cheekbone → ear) dip inside the average
    // canonical surface and get depth-culled, producing a transparent SEGMENT
    // on the temple. Pulling the occluder in ~3% keeps face-front occlusion
    // occScale shrinks the occluder toward its centroid. It was a leftover from
    // when the occluder and glasses lived in different depth frames (to avoid the
    // occluder poking through and depth-fighting the frame). Now they share the
    // SAME matrix, so depth is consistent by construction and no shrink is
    // needed — default 1.0 so the occluder HUGS the real face (otherwise the
    // recessed surface makes the glasses look like they float forward).
    // Tunable ?occscale=.
    const qs = new URLSearchParams(window.location.search);
    const occScale = (() => { const v = parseFloat(qs.get('occscale')); return Number.isFinite(v) ? v : 1.0; })();
    // occbias shifts the occluder along the face-forward axis (+Z, cm). The
    // canonical face is an AVERAGE, so even with the bridge pinned, the nose/
    // cheek/forehead depth may sit slightly forward or behind the real skin. A
    // small negative bias pushes the occluder BACK (toward the head) so it tucks
    // just under the skin and hugs the face; positive pushes it forward. Default
    // 0. Tune with ?occbias= (e.g. -0.3).
    this._occBias = (() => { const v = parseFloat(qs.get('occbias')); return Number.isFinite(v) ? v : 0.0; })();
    this._occScale = occScale;
    this._src = CANONICAL_FACE_VERTICES;
    const src = this._src;
    this._pos = new Float32Array(src.length);
    let cx = 0, cy = 0, cz = 0;
    const n = src.length / 3;
    for (let i = 0; i < src.length; i += 3) { cx += src[i]; cy += src[i+1]; cz += src[i+2]; }
    cx /= n; cy /= n; cz /= n;
    this._centroid = { cx, cy, cz };
    this._buildPositions();
    this.geometry.setAttribute('position', new BufferAttribute(this._pos, 3));
    // Use the full face MINUS the eye/lens sockets. Because this occluder is
    // posed by the SAME matrix M as the glasses, depths are consistent by
    // construction (no lens clipping), and the nose — posed by M — sits in front
    // of the far lens at yaw so it occludes correctly. We still remove the eye
    // sockets so the lens plane and occluder never coincide there. ?occregion=
    // full restores the whole face.
    const occRegion = (new URLSearchParams(window.location.search).get('occregion') || 'minuslens').toLowerCase();
    const idx = (occRegion === 'full') ? FACEMESH_TESSELATION_INDICES : FACEMESH_MINUS_LENS_INDICES;
    this.geometry.setIndex(new BufferAttribute(idx, 1));
    this.geometry.computeVertexNormals();

    this.material = new MeshBasicMaterial({
      colorWrite: false,
      depthWrite: true,
      depthTest: true,
      side: DoubleSide
    });
    // Debug material: semi-transparent magenta so we can SEE where the occluder
    // surface actually sits relative to the face (toggled by setDebugVisible).
    this._debugMaterial = new MeshBasicMaterial({
      color: 0xff00aa,
      colorWrite: true,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      depthTest: true,
      side: DoubleSide
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.renderOrder = 0;
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.visible = false;

    this.enabled = true;
    this._debug = false;
  }

  /**
   * Toggle a semi-transparent visible occluder for debugging. When on, the
   * occluder renders as a translucent magenta mesh ON TOP (no depth-write) so you
   * can see its shape and position over the face without it occluding the view.
   */
  setDebugVisible(on) {
    this._debug = on;
    this.mesh.material = on ? this._debugMaterial : this.material;
    this.mesh.renderOrder = on ? 20 : 0;   // draw over glasses when debugging
  }

  /**
   * Pose the occluder with MediaPipe's transformation matrix.
   * @param {THREE.Matrix4} matrixM  already built from MediaPipe data (column-major)
   */
  update(matrixM) {
    if (!this.enabled) { this.mesh.visible = false; return; }
    this.mesh.matrix.copy(matrixM);
    this.mesh.visible = true;
  }

  _buildPositions() {
    const src = this._src;
    const { cx, cy, cz } = this._centroid;
    const s = this._occScale, b = this._occBias;
    for (let i = 0; i < src.length; i += 3) {
      this._pos[i]   = cx + (src[i]   - cx) * s;
      this._pos[i+1] = cy + (src[i+1] - cy) * s;
      this._pos[i+2] = cz + (src[i+2] - cz) * s + b;
    }
  }

  /**
   * Runtime depth bias (cm) along the face-forward axis. Negative pushes the
   * occluder back toward the head (tucks under the skin so the glasses hug the
   * face); positive pushes it forward. Rebuilds the vertex positions.
   */
  setDepthBias(cm) {
    this._occBias = cm;
    this._buildPositions();
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeVertexNormals();
  }
  getDepthBias() { return this._occBias; }
  setNoseScale(r) {
    this._src = r === 1 ? CANONICAL_FACE_VERTICES : warpedCanonical(r);
    this._buildPositions();
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.computeVertexNormals();
  }

  setEnabled(on) { this.enabled = on; if (!on) this.mesh.visible = false; }
  hide() { this.mesh.visible = false; }
  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
